'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';

import { createSessionOverController } from './project-usability-handsfree-session-over.helpers';

interface UseHandsFreeSessionOverOptions {
  isConversationOver: boolean;
  isSpeaking: boolean;
  hasPendingAudio: boolean;
  isRecording: boolean;
  isFinalizingRecording: boolean;
  isAiVoiceEnabled: boolean;
  endRecording: () => void;
  markEnded: () => void;
  getHasEnded: () => boolean;
}

/**
 * Derives "session over" state for HandsFree UT: true when conversation is over AND
 * (if AI voice) AI has finished speaking. Also triggers endRecording when conditions are met.
 *
 * Use isSessionOver for UI that should wait for AI to finish: iframe blank/cleanup,
 * VoiceOrbDock disabled state, saving popup.
 * Use raw isConversationOver for: chat form disabled, listening recovery, question tracking.
 *
 * Flow:
 * 1. When isConversationOver → start grace period (closing TTS may not have started yet)
 * 2. After grace period → isSessionOver = true only when AI finished all queued speech (!isSpeaking && !hasPendingAudio)
 * 3. When isSessionOver and recording → schedule end after END_BUFFER_MS
 */
export function useHandsFreeSessionOver({
  isConversationOver,
  isSpeaking,
  hasPendingAudio,
  isRecording,
  isFinalizingRecording,
  isAiVoiceEnabled,
  endRecording,
  markEnded,
  getHasEnded,
}: UseHandsFreeSessionOverOptions): { isSessionOver: boolean } {
  const controllerRef = useRef<ReturnType<
    typeof createSessionOverController
  > | null>(null);
  const endRecordingRef = useRef(endRecording);
  const markEndedRef = useRef(markEnded);
  const getHasEndedRef = useRef(getHasEnded);

  controllerRef.current ??= createSessionOverController({
    endRecording: () => endRecordingRef.current(),
    markEnded: () => markEndedRef.current(),
    getHasEnded: () => getHasEndedRef.current(),
  });

  useEffect(() => {
    endRecordingRef.current = endRecording;
    markEndedRef.current = markEnded;
    getHasEndedRef.current = getHasEnded;
  });

  useEffect(() => {
    controllerRef.current?.update({
      isConversationOver,
      isSpeaking,
      hasPendingAudio,
      isRecording,
      isFinalizingRecording,
      isAiVoiceEnabled,
    });
  }, [
    isConversationOver,
    isSpeaking,
    hasPendingAudio,
    isRecording,
    isFinalizingRecording,
    isAiVoiceEnabled,
  ]);

  useEffect(() => {
    return () => {
      controllerRef.current?.dispose();
    };
  }, []);

  const isSessionOver = useSyncExternalStore(
    (listener) => {
      const controller = controllerRef.current;
      if (!controller) return () => undefined;
      return controller.subscribe(listener);
    },
    () => controllerRef.current?.getIsSessionOver() ?? false,
    () => false
  );

  return { isSessionOver };
}
